import { rqlBuilder, UserData, Document } from '@extrahorizon/javascript-sdk';
import retry from 'async-retry';
import { TableInput } from 'models/general';
import { ReportsStatus, ReportsTableItem } from 'utils/models';
import { RAW_ENDPOINTS, SCHEMA_NAMES, sdk } from 'utils/query';
import { FilterReportsFieldNames } from 'models/filter';
import { ReportData, REPORT_STATUS, CreateReportValues } from 'models/report';
import { createFileUrlFromBuffer } from 'utils/helpers';
import i18next from 'i18next';
import dayjs from 'dayjs';
import { MeasurementData } from 'models/measurement';
import { HeartRhythmMeasurement } from 'models/heartRhythm/measurement';
import { getActiveGroupIds } from 'utils/filters';
import { getUserNames } from './user';
import { EmptyParametersError } from './errors/EmptyParametersError';
import { NoMeasurementsReportError } from './errors/NoMeasurementsReportError';
import { NotRenderedReportError } from './errors/NotRenderedReportError';

export async function getMeasurementReportUrl(measurementId?: string) {
  if (!measurementId) throw new EmptyParametersError();

  const { language } = i18next;

  let report = await sdk.data.documents.findFirst<ReportData>(SCHEMA_NAMES.measurementReports, {
    rql: rqlBuilder().eq('data.measurementId', measurementId).eq('data.language', language).sort('-id').build(),
  });

  let outdatedReport = false;

  if (report?.status === REPORT_STATUS.rendered) {
    const measurement = await sdk.data.documents.findById<MeasurementData>(SCHEMA_NAMES.heartRhythm, measurementId);
    // Check if the report is generated with the latest data of the measurement.

    const reportLastUpdatedTimestamp = report.data?.forMeasurementUpdatedTimestamp || -1;
    const updatedTimestamp = measurement?.updateTimestamp?.getTime() || 0;

    outdatedReport = updatedTimestamp > reportLastUpdatedTimestamp;
    if (!outdatedReport && report.data?.readFileToken) {
      const fileData = await sdk.files.retrieve(report?.data?.readFileToken);
      return createFileUrlFromBuffer(fileData);
    }
  }

  // if no report exists or if the report is outdated, (re)create
  if (!report || outdatedReport) {
    report = await sdk.data.documents.create(SCHEMA_NAMES.measurementReports, { measurementId, language });
  }

  report = await retry(
    async () => {
      const newReport = await sdk.data.documents.findById<ReportData>(
        SCHEMA_NAMES.measurementReports,
        report?.id as string,
        {
          rql: rqlBuilder().eq('status', REPORT_STATUS.rendered).build(),
        },
      );

      if (newReport?.status === REPORT_STATUS.rendered) {
        return newReport;
      }
      throw new NotRenderedReportError(report?.id);
    },
    {
      retries: 15,
      maxTimeout: 2000,
    },
  );
  const fileData = await sdk.files.retrieve(report?.data?.readFileToken as string);
  return createFileUrlFromBuffer(fileData);
}

export async function getReportsOverview({ pageParam, filters, currentUser, limit = 20, params }: TableInput) {
  if (!currentUser) throw new EmptyParametersError();
  const { patientId } = params;
  const relevantGroupIds = getActiveGroupIds(currentUser.visibleGroupsIds, filters.groups);
  const filterList = [rqlBuilder().in(FilterReportsFieldNames.GroupId, relevantGroupIds).intermediate()];

  if (filters.status && filters.status !== ReportsStatus.All) {
    filterList.push(rqlBuilder().eq(FilterReportsFieldNames.Status, filters.status).intermediate());
  }

  const filteringRql = rqlBuilder()
    .eq(FilterReportsFieldNames.UserId, patientId || '')
    .and(...filterList.filter((filter) => filter))
    .limit(limit, pageParam * limit)
    .sort(`-${FilterReportsFieldNames.CreationTimestamp}`)
    .build();

  const configuration = await sdk.raw.get(`${RAW_ENDPOINTS.REPORTS}/${filteringRql}`);

  // Get first and last name for the userId present in the Reports Table
  const userIds = configuration.data?.data?.map((item: ReportsTableItem) => item.userId) || [];

  if (userIds) {
    const userNamesList = (await getUserNames(userIds || [])) as UserData[];

    if (userNamesList) {
      for (const oldUser of configuration.data.data) {
        const tempUser = userNamesList.find((user: UserData) => oldUser.userId === user.id);

        oldUser.firstName = tempUser?.firstName;
        oldUser.lastName = tempUser?.lastName;
      }
    }
  }
  return configuration.data;
}

export const getReportUrlById = async (variables: { id?: string }) => {
  const { id } = variables;

  if (!id) throw new EmptyParametersError();

  const { data } = await sdk.raw.get(`${RAW_ENDPOINTS.REPORTS}/${id}/pdf`, {
    responseType: 'arraybuffer',
  });

  return createFileUrlFromBuffer(data);
};

export const createReport = async (values: CreateReportValues) => {
  const { userId, groupId, reportContent, startDate, endDate } = values;
  if (!userId || !groupId) throw new EmptyParametersError();

  const startTimestamp = dayjs(startDate).startOf('day').unix() * 1000;
  const endTimestamp = dayjs(endDate).endOf('day').unix() * 1000;

  let measurements: Document<HeartRhythmMeasurement>[] = [];

  await Promise.all(
    Object.entries(reportContent)
      .filter(([_indicator, value]) => value > 0)
      .map(async ([indicator, value]) => {
        const rql = rqlBuilder()
          .eq('creatorId', userId)
          .eq('groupIds', groupId)
          .in('status', ['pending_review', 'reviewed', 'analyzed'])
          .in('data.indicator', [indicator])
          .ge('data.measurement_timestamp', `${startTimestamp}`)
          .le('data.measurement_timestamp', `${endTimestamp}`)
          .sort('data.measurement_timestamp')
          .limit(value)
          .build();
        const { data } = await sdk.data.documents.find<HeartRhythmMeasurement>(SCHEMA_NAMES.heartRhythm, { rql });
        measurements = [...measurements, ...data];
      }),
  );

  if (measurements.length === 0) return new NoMeasurementsReportError();

  const report = await sdk.raw.post(`${RAW_ENDPOINTS.REPORTS}`, {
    user_id: values.userId,
    group_id: values.groupId,
    status: 'NEW',
    content: {
      start_timestamp: startTimestamp,
      end_timestamp: endTimestamp,
      measurements,
    },
  });

  const reportUrl = await getReportUrlById({ id: report.data.id });

  return { reportUrl, reportId: report.data.id };
};
